import {
  CommonModule,
  HashLocationStrategy,
  LocationStrategy,
  PlatformLocation,
  registerLocaleData,
} from '@angular/common';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  Injector,
  NgModule,
  NO_ERRORS_SCHEMA,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule,NoopAnimationsModule } from '@angular/platform-browser/animations';
import { MotifDatePickerModule, MotifNativeDateTimeModule } from '@ey-xd/ng-motif';
import { AppRoutingModule } from '@app/app-routing.module';

import { AppComponent } from './app.component';
import { LayoutComponents } from './layout/all';
import { MotifModule } from '@app/motif.module';
import { SharedModule } from '@app/shared/shared.module';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppBootstrap } from './AppBootstrap';
import { LoaderInterceptor } from './shared/common/interceptors/loader.interceptor';
import { QuillModule } from 'ngx-quill';
import { LoginModule } from './login/login.module';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { SettingsService } from './shared/common/services/Authentication/settings.service';
import { TokenInterceptor } from './shared/common/interceptors/token.interceptor';
import { WebSocketConfigService } from './shared/common/services/Authentication/web-socket-config.service';
import { UserInfoComponent } from './layout/mainframe/page/header/user-info/user-info.component';
import { HomeModule } from './home/home.module';
import { PrivacyNoticeModule } from './privacy-notice/privacy-notice.module';
import { RegionSelectionComponent } from './region-selection/region-selection.component';
import { CommonMethodsService } from './shared/common/services/common-methods.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

export function appInitializerFactory(
  injector: Injector,
  platformLocation: PlatformLocation
) {
  return () => {
    return new Promise<boolean>((resolve, reject) => {
      AppBootstrap.run(injector, (userLoggedIn) => {
        if (userLoggedIn?.lang) {
          try {
            setLocale(resolve, true, reject, userLoggedIn);
          } catch (error) {
            reject(error);
          }
        } else {
          resolve(true);
        }
      });
    });
  };
}

function setLocale(
  resolve: (value: boolean | PromiseLike<boolean>) => void,
  result: boolean,
  reject: (reason?: any) => void,
  userLoggedIn: { lang: string }
) {
  if (shouldLoadLocale(userLoggedIn)) {
    const lang = userLoggedIn.lang;
    import(`@/../@angular/common/locales/${lang}.mjs`).then((module) => {
      registerLocaleData(module.default);
      resolve(result);
    }, reject);
  } else {
    resolve(result);
  }
}

function shouldLoadLocale(userLoggedIn: any) {
  return userLoggedIn.lang !== 'en-US';
}

@NgModule({
  declarations: [
    AppComponent,
    ...LayoutComponents,
    UserInfoComponent,
    RegionSelectionComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    CommonModule,
    BrowserAnimationsModule,
    NoopAnimationsModule,
    MotifDatePickerModule,
    MotifNativeDateTimeModule,
    SharedModule.forRoot(),
    AppRoutingModule,
    MotifModule,
    HttpClientModule,
    HomeModule,
    PrivacyNoticeModule,
    ReactiveFormsModule,
    QuillModule.forRoot(),
    LoginModule,
    OAuthModule.forRoot(),
  ],
  providers: [
    WebSocketConfigService,
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: appInitializerFactory,
    //   deps: [Injector, PlatformLocation],
    //   multi: true,
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: regionHandoffInitializerFactory,
      deps: [SettingsService, CommonMethodsService, OAuthService],
      multi: true,
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class AppModule {}

const REGIONAL_LOGIN_HINT_STORAGE_KEY = 'regionalLoginHint';

export function regionHandoffInitializerFactory(
  settingsService: SettingsService,
  commonService: CommonMethodsService,
  oauthService: OAuthService
) {
  return async () => {
    if (environment.isBaseApplication) {
      await settingsService.loadAuthDetails();
      return;
    }

    const currentUrl = new URL(window.location.href);
    const handoffCode = currentUrl.searchParams.get('handoffCode');
    currentUrl.searchParams.delete('handoffCode');
    window.history.replaceState(
      window.history.state,
      '',
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
    );

    const pendingLoginHint = sessionStorage
      .getItem(REGIONAL_LOGIN_HINT_STORAGE_KEY)
      ?.trim();
    if (pendingLoginHint && hasOAuthCallbackResponse()) {
      try {
        await settingsService.loadAuthDetails(true);
        oauthService.setupAutomaticSilentRefresh();

        const loginSucceeded = await oauthService.tryLogin();
        if (!loginSucceeded) {
          throw new Error(
            'Interactive sign-in callback did not return a token.'
          );
        }

        await initializeRegionalUser(
          settingsService,
          commonService,
          oauthService
        );
        sessionStorage.removeItem(REGIONAL_LOGIN_HINT_STORAGE_KEY);
      } catch {
        settingsService.clearRegionalSession();
        sessionStorage.removeItem(REGIONAL_LOGIN_HINT_STORAGE_KEY);
        redirectToGlobalApplication();
      }
      return;
    }

    sessionStorage.removeItem(REGIONAL_LOGIN_HINT_STORAGE_KEY);
    settingsService.clearRegionalSession();

    try {
      await settingsService.loadAuthDetails(true);

      oauthService.setupAutomaticSilentRefresh();

      const exchangeResponse = await firstValueFrom(
        commonService.exchangeRegionHandoffCode(handoffCode ?? '')
      );
      if (exchangeResponse?.status !== 200) {
        throw new Error('Regional handoff exchange was not successful.');
      }

      const loginHint = exchangeResponse.body?.userEmail?.trim();
      if (!loginHint) {
        throw new Error('Regional handoff exchange did not return a user email.');
      }

      try {
        await oauthService.silentRefresh({ login_hint: loginHint });
      } catch {
        beginInteractiveRegionalLogin(
          settingsService,
          oauthService,
          loginHint
        );
        return;
      }

      if (!oauthService.getAccessToken()) {
        beginInteractiveRegionalLogin(
          settingsService,
          oauthService,
          loginHint
        );
        return;
      }

      await initializeRegionalUser(
        settingsService,
        commonService,
        oauthService
      );
      sessionStorage.removeItem(REGIONAL_LOGIN_HINT_STORAGE_KEY);
    } catch {
      settingsService.clearRegionalSession();
      sessionStorage.removeItem(REGIONAL_LOGIN_HINT_STORAGE_KEY);
      redirectToGlobalApplication();
    }
  };
}

function beginInteractiveRegionalLogin(
  settingsService: SettingsService,
  oauthService: OAuthService,
  loginHint: string
): void {
  settingsService.clearRegionalSession();
  sessionStorage.setItem(REGIONAL_LOGIN_HINT_STORAGE_KEY, loginHint);
  oauthService.initLoginFlow('', { login_hint: loginHint });
}

async function initializeRegionalUser(
  settingsService: SettingsService,
  commonService: CommonMethodsService,
  oauthService: OAuthService
): Promise<void> {
  const accessToken = oauthService.getAccessToken();
  if (!accessToken) {
    throw new Error('Authentication did not return an access token.');
  }

  settingsService.setRegionalAccessToken(accessToken);
  const currentUser = await firstValueFrom(commonService.getUserDetails(true));
  if (!commonService.initializeCurrentUser(currentUser)) {
    throw new Error('Regional login did not return valid user details.');
  }
}

function hasOAuthCallbackResponse(): boolean {
  let hash = window.location.hash;
  try {
    hash = decodeURIComponent(hash);
  } catch {
    // Leave malformed fragments untouched; they will not match the response check.
  }
  return /(?:^|[?#&])(access_token|id_token|code|error)=/.test(hash);
}

function redirectToGlobalApplication(): void {
  try {
    const globalUrl = new URL(environment.globalApplicationUrl);
    const currentUrl = new URL(window.location.href);
    if (
      (globalUrl.protocol === 'http:' || globalUrl.protocol === 'https:') &&
      (globalUrl.origin !== currentUrl.origin ||
        globalUrl.pathname.replace(/\/+$/, '') !==
          currentUrl.pathname.replace(/\/+$/, '') ||
        globalUrl.search !== currentUrl.search)
    ) {
      window.location.replace(globalUrl.toString());
    }
  } catch {
    // Keep the regional login screen disabled when the fallback URL is invalid.
  }
}
